import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Button, Tag, Spin, notification, Typography, Space, Tooltip } from 'antd';
import { SearchOutlined, FileExcelOutlined, FilterOutlined, EyeOutlined, ShoppingOutlined, TagOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/constants';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;

interface Product {
  _id: string;
  modelName: string;
  modelStyle?: string;
  purchasingGroup: string;
  productCode?: string;
  prototypeCode?: string;
  isPrototype: boolean;
  fabric: {
    _id: string;
    name: string;
    code?: string;
    composition?: string;
    supplier?: string;
  } | string;
  color: {
    _id: string;
    name: string;
    firmCode?: string;
    tcxCode?: string;
    hexCode?: string;
  } | string;
  accessories: Accessory[];
  imageUrls?: string[];
  imageUrl?: string;
}

interface Accessory {
  _id: string;
  name: string;
  quantity: string;
  type?: string;
  code?: string;
  description?: string;
}

interface Fabric {
  _id: string;
  name: string;
  code?: string;
  composition?: string;
  supplier?: string;
  weight?: string;
  construction?: string;
}

// Malzeme listesi için kullanılacak birleştirilmiş tür
interface Material {
  id: string;
  productId: string;
  productName: string;
  productCode: string;
  type: 'fabric' | 'accessory';
  name: string;
  code?: string;
  quantity?: string;
  description?: string;
  supplier?: string;
  composition?: string;
  color?: string;
  materialId: string; // Malzemenin kendi ID'si (Kumaş veya aksesuar ID'si)
}

const MaterialList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [filters, setFilters] = useState({
    materialType: '',
    product: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      processMaterials();
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/products`);
      if (!response.ok) {
        throw new Error('Ürünler yüklenemedi');
      }
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Ürünleri getirirken hata oluştu:', error);
      notification.error({
        message: 'Hata',
        description: 'Ürünler yüklenirken bir hata oluştu.'
      });
    } finally {
      setLoading(false);
    }
  };

  const processMaterials = () => {
    const materialsList: Material[] = [];

    products.forEach(product => {
      // Kumaş bilgilerini ekle
      if (product.fabric) {
        const fabricData = typeof product.fabric === 'string' 
          ? { _id: product.fabric, name: product.fabric } 
          : product.fabric;
          
        const colorInfo = typeof product.color === 'string'
          ? product.color
          : `${product.color.name}${product.color.firmCode ? ` (${product.color.firmCode})` : ''}`;

        materialsList.push({
          id: `fabric-${product._id}-${fabricData._id}`,
          productId: product._id,
          productName: product.modelName,
          productCode: product.isPrototype ? (product.prototypeCode || 'P-???') : (product.productCode || '???'),
          type: 'fabric',
          name: fabricData.name,
          code: fabricData.code || '',
          supplier: fabricData.supplier,
          composition: fabricData.composition,
          color: colorInfo,
          materialId: fabricData._id // Kumaşın kendi ID'si
        });
      }

      // Aksesuar bilgilerini ekle
      if (product.accessories && product.accessories.length > 0) {
        product.accessories.forEach(accessory => {
          materialsList.push({
            id: `accessory-${product._id}-${accessory._id}`,
            productId: product._id,
            productName: product.modelName,
            productCode: product.isPrototype ? (product.prototypeCode || 'P-???') : (product.productCode || '???'),
            type: 'accessory',
            name: accessory.name,
            code: accessory.code || '',
            quantity: accessory.quantity,
            description: accessory.description || '',
            materialId: accessory._id // Aksesuarın kendi ID'si
          });
        });
      }
    });

    setMaterials(materialsList);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const getFilteredMaterials = () => {
    return materials.filter(material => {
      const matchesSearch = searchText === '' || 
        material.name.toLowerCase().includes(searchText.toLowerCase()) ||
        material.productName.toLowerCase().includes(searchText.toLowerCase()) ||
        (material.code && material.code.toLowerCase().includes(searchText.toLowerCase())) ||
        (material.description && material.description.toLowerCase().includes(searchText.toLowerCase()));
      
      const matchesType = filters.materialType === '' || material.type === filters.materialType;
      const matchesProduct = filters.product === '' || material.productId === filters.product;
      
      return matchesSearch && matchesType && matchesProduct;
    });
  };

  const exportToExcel = () => {
    try {
      const filteredData = getFilteredMaterials();
      
      // Excel için veri hazırlama
      const excelData = filteredData.map(material => ({
        'Malzeme Türü': material.type === 'fabric' ? 'Kumaş' : 'Aksesuar',
        'Ürün': material.productName,
        'Ürün Kodu': material.productCode,
        'Malzeme Adı': material.name,
        'Kod': material.code || '',
        'Renk': material.color || '',
        'Miktar': material.quantity || '',
        'Açıklama': material.type === 'fabric' 
          ? `${material.composition ? 'Kompozisyon: ' + material.composition : ''}${material.supplier ? ', Tedarikçi: ' + material.supplier : ''}`
          : material.description || ''
      }));
      
      // Worksheet oluşturma
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Excel dosyasını oluşturma
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Malzeme Listesi');
      
      // Sütun genişliklerini ayarlama
      const maxWidth: Record<string, number> = {};
      excelData.forEach(row => {
        Object.keys(row).forEach(key => {
          const cellValue = row[key as keyof typeof row];
          const length = cellValue ? cellValue.toString().length : 0;
          maxWidth[key] = Math.max(maxWidth[key] || 0, length);
        });
      });
      
      worksheet['!cols'] = Object.keys(maxWidth).map(key => ({ wch: Math.min(maxWidth[key] + 2, 50) }));
      
      // Excel dosyasını indirme
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(workbook, `Malzeme_Listesi_${dateStr}.xlsx`);
      
      notification.success({
        message: 'Başarılı',
        description: 'Malzeme listesi Excel dosyası olarak indirildi.'
      });
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      notification.error({
        message: 'Hata',
        description: 'Excel dosyası oluşturulurken bir hata oluştu.'
      });
    }
  };

  const viewProduct = (productId: string) => {
    navigate(`/products/${productId}`);
  };

  const viewMaterialDetail = (material: Material) => {
    if (material.type === 'fabric') {
      navigate(`/fabrics/${material.materialId}`);
    } else if (material.type === 'accessory') {
      navigate(`/accessories/${material.materialId}`);
    }
  };

  const columns = [
    {
      title: 'Malzeme Türü',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        type === 'fabric' ? 
          <Tag color="blue">Kumaş</Tag> : 
          <Tag color="orange">Aksesuar</Tag>
      ),
      width: 120,
    },
    {
      title: 'Ürün',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string, record: Material) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          <Text type="secondary">{record.productCode}</Text>
        </Space>
      ),
      width: 160,
    },
    {
      title: 'Malzeme Adı',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: 'Renk',
      dataIndex: 'color',
      key: 'color',
      width: 120,
      render: (text: string) => text ? text : '-',
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text: string) => text ? text : '-',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: Material) => {
        if (record.type === 'fabric') {
          const details = [];
          if (record.composition) details.push(`Kompozisyon: ${record.composition}`);
          if (record.supplier) details.push(`Tedarikçi: ${record.supplier}`);
          return details.join(', ') || '-';
        }
        return text || '-';
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_: any, record: Material) => (
        <Space>
          <Tooltip title={record.type === 'fabric' ? 'Kumaş Detayı' : 'Aksesuar Detayı'}>
            <Button 
              icon={record.type === 'fabric' ? <TagOutlined /> : <ShoppingOutlined />}
              size="small"
              onClick={() => viewMaterialDetail(record)}
              type="text"
              style={{ 
                color: record.type === 'fabric' ? '#1890ff' : '#fa8c16'
              }}
            />
          </Tooltip>
          <Tooltip title="Ürünü Görüntüle">
            <Button 
              icon={<EyeOutlined />}
              size="small"
              onClick={() => viewProduct(record.productId)}
              type="text"
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-full bg-white rounded-lg shadow-lg p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <Title level={2} className="m-0">Malzeme Listesi</Title>
          <Space>
            <Button 
              icon={<FileExcelOutlined />} 
              onClick={exportToExcel}
              className="mt-2 sm:mt-0"
            >
              Excel'e Aktar
            </Button>
          </Space>
        </div>

        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <Input
            placeholder="Ara..."
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            className="md:w-1/3"
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <Select 
              placeholder="Malzeme Türü" 
              style={{ minWidth: 150 }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, materialType: value || '' }))}
              value={filters.materialType || undefined}
            >
              <Option value="fabric">Kumaş</Option>
              <Option value="accessory">Aksesuar</Option>
            </Select>
            <Select 
              placeholder="Ürüne Göre Filtrele" 
              style={{ minWidth: 200 }}
              allowClear
              onChange={(value) => setFilters(prev => ({ ...prev, product: value || '' }))}
              value={filters.product || undefined}
            >
              {products.map((product) => (
                <Option key={product._id} value={product._id}>
                  {product.modelName}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={getFilteredMaterials()}
          rowKey="id"
          loading={loading}
          pagination={{ 
            defaultPageSize: 10, 
            showSizeChanger: true, 
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`
          }}
          scroll={{ x: 1100 }}
        />
      </div>
    </div>
  );
};

export default MaterialList; 